<template>
  <div id="customer-accounts" class="d-flex">
    <div v-if="showCustomerAccounts" class="mr-1 d-flex align-center">
      <SoMenu
        :label="'Account ' + currentAccountNumber?.number"
        :max-height="'600'"
        :activator="'#customer-accounts'"
        :class="'py-1'"
      >
        <SoMenuItem
          v-for="(account, index) in customerAccounts"
          :key="index"
          open-delay="100"
          location="bottom"
        >
          <div class="px-4">
            <router-link
              :to="`/customer/${account.number}`"
              target="_blank"
              style="text-decoration: none; color: inherit"
            >
              <h6
                :class="account.isActive ? '' : 'text-critical'"
                class="font-weight-bold"
              >
                Account {{ account.number }}
              </h6>
              <div
                data-testid="sp-joined-date"
                class="text-body-2 text-medium-emphasis"
              >
                {{ formatDate(account.startDate) }}
                <span v-if="account.endDate">
                  to
                  {{ formatDate(account.endDate) }}
                </span>
                <span v-else> onwards</span>
                ({{ account.accountAge }})
              </div>
              <div data-testid="sp-account-balance">
                {{
                  formatMoney({
                    amount: account.balance,
                    showNegativeSign: false,
                  })
                }}
              </div>
            </router-link>
          </div>
        </SoMenuItem>
      </SoMenu>
      <SoCopyToClipboard :plain-text="currentAccountNumber?.number">
        <template #button="{ copyToClipboard }">
          <SoButton
            type="sidebar-icon"
            icon="copy sm"
            class="ml-1"
            @click.stop="copyToClipboard"
          />
        </template>
      </SoCopyToClipboard>
    </div>
    <div v-else>
      <h4 data-testid="sp-account-number" class="mr-1 d-flex align-center">
        Account {{ currentAccountData.number }}
        <SoCopyToClipboard :plain-text="currentAccountData.number">
          <template #button="{ copyToClipboard }">
            <SoButton
              type="sidebar-icon"
              icon="copy sm"
              class="ml-1"
              @click="copyToClipboard"
            />
          </template>
        </SoCopyToClipboard>
      </h4>
      <div class="mb-1">
        <SoStatus
          ref="status-chip"
          class="align-start"
          :status="getAccountStatus(currentAccountData.status.text)"
        />
      </div>
    </div>
  </div>
  <div data-testid="sp-joined-date" class="text-body-2 text-medium-emphasis">
    {{ formatDate(currentAccountData.startDate) }}
    <span v-if="currentAccountData.endDate">
      to
      {{ formatDate(currentAccountData.endDate) }}
    </span>
    <span v-else> onwards</span>
    ({{ currentAccountData.accountAge }})
  </div>
  <h2 data-testid="sp-account-balance" class="my-4 d-flex align-center">
    {{
      formatMoney({
        amount: currentAccountData.balance,
        showNegativeSign: false,
      })
    }}
    <SoStatus
      v-if="currentAccountData.balance !== 0"
      :status="currentAccountData.balanceStatus"
      class="ml-2"
    />
  </h2>

  <div v-if="prizeDrawMessage && isPrizeDrawEnabled" class="my-4">
    <SoTooltip
      text="Prize draw is open until 31st Jan. Winners will be contacted within 2 weeks of the draw."
    >
      <SoChip
        ref="prize-draw-chip"
        :text="prizeDrawMessage"
        :type="
          isAlreadyEnteredForPrizeDraw
            ? SoChipType.SUCCESS
            : SoChipType.IMPORTANT
        "
      />
    </SoTooltip>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoStatus from "@/components/SoStatus.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { AccountStatuses } from "@/models/account/account.types"
  import { formatDate } from "@/helpers/dateHelper"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import SoMenu from "@/components/SoMenu.vue"
  import SoMenuItem from "@/components/SoMenuItem.vue"
  import { Status } from "@/types/Status"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import SoTooltip from "@/components/SoTooltip.vue"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  export default defineComponent({
    components: {
      SoMenuItem,
      SoMenu,
      SoStatus,
      SoCopyToClipboard,
      SoButton,
      SoChip,
      SoTooltip,
    },
    data() {
      return {
        currentAccount: useCurrentAccountStore().account,
        currentCustomerAccounts: useCurrentAccountStore().customerAccounts,
        coreId: useCurrentAccountStore().coreId,
        formatDate,
        formatMoney,
        SoChipType,
      }
    },
    computed: {
      customerAccounts() {
        return this.showCustomerAccounts
          ? this.currentCustomerAccounts.data
          : []
      },
      currentAccountData() {
        return this.currentAccount?.data
      },
      showCustomerAccounts() {
        return this.currentCustomerAccounts.data.length > 1
      },
      currentAccountNumber() {
        return this.customerAccounts?.find(
          (item) => item.number === this.currentAccountData.number,
        )
      },
      isAlreadyEnteredForPrizeDraw() {
        const activeDrawInfoForAccount = this.getActiveDrawInfo()

        return activeDrawInfoForAccount?.alreadyEntered
      },
      isPrizeDrawEnabled() {
        return FeaturesService.isFeatureActive("PRIZE_DRAW")
      },
      prizeDrawMessage() {
        const activeDrawInfoForAccount = this.getActiveDrawInfo()

        if (
          !activeDrawInfoForAccount?.eligibleForDraw &&
          !activeDrawInfoForAccount?.alreadyEntered
        )
          return ""

        if (!activeDrawInfoForAccount.alreadyEntered)
          return "Eligible for prize draw"

        return "Already entered prize draw"
      },
    },
    methods: {
      getAccountStatus(statusText: string) {
        switch (statusText) {
          case AccountStatuses.ACTIVE:
            return new Status("normal", "On Supply")
          case AccountStatuses.CLOSED:
            return new Status("critical", "Closed")
          case AccountStatuses.NOT_ON_SUPPLY:
            return new Status("neutral", "Transferring")
          case AccountStatuses.TERMINATING:
            return new Status("caution", "Terminating")
          default:
            return new Status("default", "Cancelled")
        }
      },
      getActiveDrawInfo() {
        if (!this.coreId.data) return null

        return useCurrentAccountStore().getActiveDrawInfoForAccount(
          this.coreId.data,
        ).data
      },
    },
  })
</script>
<style scoped>
  .v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line {
    padding: 8px 0;
  }
  :deep(.so-button) {
    height: 42px;
  }
  :deep(.v-btn__content) {
    font-weight: bold;
  }
</style>
